import BlockOutlined from "@mui/icons-material/BlockOutlined";
import CheckCircleOutlineOutlined from "@mui/icons-material/CheckCircleOutlineOutlined";
import EventBusyOutlined from "@mui/icons-material/EventBusyOutlined";
import ExpandMore from "@mui/icons-material/ExpandMore";
import PersonOutlined from "@mui/icons-material/PersonOutlined";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import { Box, Collapse, IconButton, Tooltip, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { JumpingDots } from "../../../../../../components/UIToolkit/Animations/JumpingDots";
import { SystemDropdown } from "../../../../../../components/UIToolkit/Inputs/SystemDropDown/SystemDropdown";
import { SystemValidationTextField } from "../../../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { PtoTypes } from "../../../../../../utils/models";
import type { PtoTypeRow } from "./usePtoTypeRows";

/** Matches the Raw Table grid's "Do not sync" sentinel — a chosen-but-null mapping. */
export const EXCLUDED_VALUE = "__excluded__";

interface HrisRawPtoTypesProps {
	rows: PtoTypeRow[];
	/** Set of rawStrings whose save is debounced/in flight — drives the per-row spinner. */
	pendingKeys: Set<string>;
	onPtoTypeChange: (rawString: string, value?: string) => void;
	/** Search state owned by the parent so both panes filter against the same query. */
	search: string;
	setSearch: (next: string) => void;
}

/**
 * Mapping status surfaced on each status icon via `data-pto-state` (styling / test hook).
 * Enumerated so the three states aren't repeated as bare string literals.
 */
enum PtoMappingState {
	Unmapped = "unmapped",
	DontSync = "dontSync",
	Mapped = "mapped",
}

/** At-a-glance mapping status for a raw PTO type: unmapped, do-not-sync, or mapped. */
const PtoStatusIcon: FC<{ row: PtoTypeRow; testId: string }> = ({ row, testId }) => {
	const { t } = useTranslation();
	const common = { fontSize: "small" as const, "data-testid": testId };
	if (row.isUnmapped) {
		return (
			<Tooltip title={t("hris.mapping.notMapped")}>
				<WarningAmberOutlined color="warning" {...common} data-pto-state={PtoMappingState.Unmapped} />
			</Tooltip>
		);
	}
	if (row.ptoType === null) {
		return (
			<Tooltip title={t("hris.mapping.excluded")}>
				<BlockOutlined color="disabled" {...common} data-pto-state={PtoMappingState.DontSync} />
			</Tooltip>
		);
	}
	return (
		<Tooltip title={t("hris.mapping.timeOff.mappedTo", { ptoType: row.ptoType })}>
			<CheckCircleOutlineOutlined color="success" {...common} data-pto-state={PtoMappingState.Mapped} />
		</Tooltip>
	);
};

interface PtoTypeRowItemProps {
	row: PtoTypeRow;
	ptoTypeItems: { value: string; label: string }[];
	isPending: boolean;
	onPtoTypeChange: (rawString: string, value?: string) => void;
}

/**
 * Single raw-PTO-type row. Owns its own collapse state so toggling one row does not re-render its
 * siblings. The PTO Type picker is the primary control (reason stays editable in the Raw Table view);
 * expanding reveals the last-60-day time-offs (employee + date) behind the count badge.
 */
const PtoTypeRowItem: FC<PtoTypeRowItemProps> = memo(({ row, ptoTypeItems, isPending, onPtoTypeChange }) => {
	const { t } = useTranslation();
	const [expanded, setExpanded] = useState(false);
	const handleToggle = useCallback(() => setExpanded((prev) => !prev), []);
	const hasTimeOffs = row.count > 0;
	const selected = row.isUnmapped ? undefined : (row.ptoType ?? EXCLUDED_VALUE);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				borderRadius: 1,
				border: "1px solid",
				borderColor: row.isUnmapped ? "warning.light" : "divider",
				px: 1,
				py: 0.5,
			}}
			data-testid={`hris-mapping-pto-row-${row.rawString}`}
		>
			<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
					<IconButton size="small" onClick={handleToggle} aria-label="toggle" disabled={!hasTimeOffs}>
						<ExpandMore sx={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }} />
					</IconButton>
					<PtoStatusIcon row={row} testId={`hris-mapping-pto-status-${row.rawString}`} />
				</Box>
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Typography sx={{ fontWeight: 500 }} noWrap>
						{row.rawString}
					</Typography>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
						<EventBusyOutlined sx={{ fontSize: 14, color: "text.secondary" }} />
						<Typography variant="caption" color="text.secondary" data-testid={`hris-mapping-pto-count-${row.rawString}`}>
							{t("hris.mapping.timeOff.last60Count", { count: row.count })}
						</Typography>
						{row.reason && (
							<Typography variant="caption" color="text.secondary">
								· {row.reason}
							</Typography>
						)}
					</Box>
				</Box>
				{isPending ? (
					<Box sx={{ minWidth: 160, display: "flex", justifyContent: "center" }} data-testid={`hris-mapping-pto-pending-${row.rawString}`}>
						<JumpingDots />
					</Box>
				) : (
					<SystemDropdown<string>
						items={ptoTypeItems}
						selectedItem={selected}
						onChange={(value) => onPtoTypeChange(row.rawString, value)}
						renderValue={selected ? undefined : () => <em style={{ opacity: 0.6 }}>{t("hris.mapping.notMapped")}</em>}
						displayEmpty
						formControlSx={{ minWidth: 160, my: 0.5 }}
						data-testid={`hris-mapping-pto-type-${row.rawString}`}
					/>
				)}
			</Box>
			<Collapse in={expanded} mountOnEnter unmountOnExit>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pl: 5, py: 1 }}>
					{row.timeOffs.map((to, idx) => (
						<Box key={`${to.employeeName}-${to.eventDate}-${idx}`} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<PersonOutlined fontSize="small" sx={{ color: "text.secondary" }} />
							<Typography variant="body2">{to.employeeName}</Typography>
							{to.eventDate && (
								<Typography variant="caption" color="text.secondary">
									{DateTime.fromISO(to.eventDate).toFormat("dd LLL yyyy")}
								</Typography>
							)}
						</Box>
					))}
				</Box>
			</Collapse>
		</Box>
	);
});

/**
 * Left pane of the Time Off Pretty Mapping view. Lists raw PTO event types with a per-type PTO Type
 * picker and last-60-day count, each expandable to its time-offs — the Time Off analog of the Roles &
 * Jobs raw-employees pane.
 */
export const HrisRawPtoTypes: FC<HrisRawPtoTypesProps> = ({ rows, pendingKeys, onPtoTypeChange, search, setSearch }) => {
	const { t } = useTranslation();

	const ptoTypeItems = useMemo(
		() => [...Object.values(PtoTypes).map((v) => ({ value: v, label: v })), { value: EXCLUDED_VALUE, label: t("hris.mapping.excluded") }],
		[t],
	);

	return (
		<Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }} data-testid="hris-mapping-raw-pto-types-pane">
			<SystemValidationTextField
				value={search}
				onChange={(e) => setSearch(e.target.value)}
				placeholder={t("hris.mapping.search.placeholder.rawString")}
				reserveHeightForHelperText={false}
				onClear={() => setSearch("")}
				formControlSx={{ width: "100%", pr: 1.5 }}
				data-testid="hris-mapping-raw-pto-types-search"
			/>
			<Box sx={{ width: "100%", overflow: "auto", display: "flex", flexDirection: "column", gap: 1, p: 1, pl: 0.1, pr: 1.5 }}>
				{rows.length === 0 ? (
					<Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
						{t("hris.mapping.rawEmployees.empty")}
					</Typography>
				) : (
					rows.map((row) => (
						<PtoTypeRowItem
							key={row.rawString}
							row={row}
							ptoTypeItems={ptoTypeItems}
							isPending={pendingKeys.has(row.rawString)}
							onPtoTypeChange={onPtoTypeChange}
						/>
					))
				)}
			</Box>
		</Box>
	);
};
